import { Flex, Text, VStack } from '@chakra-ui/layout';
import { Tag } from '@chakra-ui/react';
import { chakraComponents, OptionProps } from 'chakra-react-select';
import { useMemo } from 'react';
import { FieldPath, FieldValues } from 'react-hook-form';
import { ControlledSelect, ControlledSelectProps } from './Select';
import type { Drn } from '@duke-hq/drn/type';

const roleColors = {
  STUDENT: 'orange.600',
  CONSULTANT: 'purple.600',
};

function Option<O extends { label: string; role: keyof typeof roleColors }>(props: OptionProps<O>) {
  return (
    <chakraComponents.Option {...props}>
      <Flex flexDirection="column" justify="space-between" w="full" gap={1}>
        <VStack align="flex-start" gap={0}>
          <Text fontWeight="medium">{`${props.data.label}`}</Text>
        </VStack>
        <Tag borderRadius="full" w="fit-content">
          <Text fontSize="xs" color={roleColors[props.data.role] || 'gray.500'}>
            {`${props.data.role.replace(/_/g, ' ')}`}
          </Text>
        </Tag>
      </Flex>
    </chakraComponents.Option>
  );
}

export type SelectUserFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = ControlledSelectProps<TFieldValues, TName> & {
  providerId: number;
  onInputChange: (input: string) => void;
  value?: string;
  userList: {
    id: string;
    first_name: string;
    last_name?: string | null;
    role: string;
    drn: Drn;
  }[];
  userDrn?: Drn;
};

export function SelectUserField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  providerId,
  value,
  onInputChange,
  userList,
  userDrn,
  ...props
}: SelectUserFieldProps<TFieldValues, TName>) {
  const options = useMemo(() => {
    if (typeof providerId !== 'number') {
      return [];
    }

    let defaultValue: SelectUserFieldProps<TFieldValues, TName>['userList'] = [];

    if (value != null && userDrn != null) {
      defaultValue = userList?.filter((user) => user.drn === userDrn) ?? [];
    }

    return (userList?.length ? userList : defaultValue)?.map((user) => ({
      label: `${user.first_name} ${user.last_name}`,
      value: user.drn,
      role: user.role,
    }));
  }, [userList, value, providerId, userDrn]);

  return (
    <ControlledSelect
      onInputChange={onInputChange}
      options={options}
      components={{
        Option,
      }}
      {...props}
    />
  );
}
